/**
 * Landing SEO locale — "Retraite spirituelle en Drôme provençale".
 *
 * URL : /retraite-spirituelle-drome-provence
 * Objectif : capter les requêtes long-tail géographiques :
 *   - retraite spirituelle Drôme
 *   - retraite bien-être Drôme provençale
 *   - séjour méditation Provence
 *   - retraite spirituelle sud Lyon
 *
 * Cette page est une SATELLITE de /retraites (pilier national). Elle a son
 * propre contenu original ~450 mots pour éviter tout duplicate content, et
 * chaque section renvoie soit à la page pilier soit à un séjour spécifique.
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, MapPin, Calendar, ArrowRight, Leaf, Compass, Wind } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";

const SITE_URL = "https://www.bilan2vie.fr";
// Image fournie par Guillaume — méditation en groupe sur terrasse en pierre
// avec vue panoramique sur la Drôme provençale (champs de lavande + village perché).
const HERO_IMAGE = "https://customer-assets-lxgj4vgw.emergentagent.net/job_spirituel-wellness/artifacts/jrun8urc_mediation%20Dro%CC%82me%20retraite%20spirituelle.webp";

export default function RetreatLandingDromeProvence() {
  const canonical = `${SITE_URL}/retraite-spirituelle-drome-provence`;
  const [nextRetreat, setNextRetreat] = useState(null);

  useEffect(() => {
    axios.get(`${API}/retreats`)
      .then((r) => setNextRetreat((r.data.retreats || [])[0] || null))
      .catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Retraite spirituelle en Drôme provençale — Bilan 2 Vie",
    description:
      "Retraites spirituelles au cœur de la Drôme provençale, animées par Guillaume Bilan 2 Vie. Méditation, soins énergétiques, ateliers de reconnexion — à 1h de Valence, 2h de Lyon.",
    url: canonical,
    inLanguage: "fr-FR",
    isPartOf: { "@type": "WebSite", name: "Bilan 2 Vie", url: SITE_URL },
    about: [
      { "@type": "Thing", name: "Retraite spirituelle" },
      { "@type": "Place", name: "Drôme provençale", address: { "@type": "PostalAddress", addressRegion: "Drôme", addressCountry: "FR" } },
    ],
    provider: { "@type": "LocalBusiness", "@id": `${SITE_URL}/#localbusiness`, name: "Bilan 2 Vie", url: SITE_URL },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Séjours & Retraites", item: `${SITE_URL}/retraites` },
      { "@type": "ListItem", position: 3, name: "Retraite spirituelle en Drôme provençale", item: canonical },
    ],
  };

  return (
    <div className="bg-[#faf6ea] text-[#2a2418] min-h-screen">
      <Helmet>
        <title>Retraite spirituelle en Drôme provençale — Séjour bien-être Provence | Bilan 2 Vie</title>
        <meta
          name="description"
          content="Retraite spirituelle en Drôme provençale, animée par Guillaume. Méditation, soins énergétiques, ateliers en pleine nature. À 1h de Valence, 2h de Lyon, 2h30 de Marseille. Réservation en ligne, paiement 3-4x via Klarna."
        />
        <meta
          name="keywords"
          content="retraite spirituelle Drôme, retraite spirituelle Drôme provençale, retraite bien-être Provence, séjour méditation Drôme, retraite énergétique sud Lyon, retraite spirituelle sud France, Mas d'Archas"
        />
        <meta name="geo.region" content="FR-26" />
        <meta name="geo.placename" content="Drôme provençale" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Bilan 2 Vie" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:title" content="Retraite spirituelle en Drôme provençale — Bilan 2 Vie" />
        <meta property="og:description" content="Un séjour spirituel au cœur de la Drôme provençale. Méditation, soins et ateliers en pleine nature." />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={HERO_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      {/* Fil d'Ariane visible pour SEO + UX */}
      <nav aria-label="Fil d'Ariane" className="max-w-5xl mx-auto px-6 pt-8 text-xs text-[#8b7d5e]">
        <Link to="/" className="hover:text-[#2a2418] transition-colors">Accueil</Link>
        <span className="mx-2">/</span>
        <Link to="/retraites" className="hover:text-[#2a2418] transition-colors">Séjours & Retraites</Link>
        <span className="mx-2">/</span>
        <span className="text-[#2a2418]">Drôme provençale</span>
      </nav>

      {/* Hero — image en pleine largeur avec titre superposé pour un impact visuel fort */}
      <section className="relative w-full min-h-[560px] md:min-h-[620px] flex items-end overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Retraite spirituelle en Drôme provençale — méditation en groupe sur terrasse avec vue panoramique sur les champs de lavande et un village perché"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Dégradé bas → haut pour lisibilité + ambiance dramatique */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1610]/85 via-[#2a2418]/40 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl text-white">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-white/85 mb-6">
              <MapPin className="w-3.5 h-3.5" /> Drôme provençale
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-white mb-6 drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]" data-testid="landing-drome-h1">
              Retraite spirituelle<br />en Drôme provençale
            </h1>
            <p className="text-lg text-white/95 leading-relaxed max-w-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
              Entre lavande, oliviers et rivières translucides, la Drôme provençale
              est un écrin naturel unique pour se réaligner en profondeur.
              Nos séjours spirituels y prennent racine, à <strong>1h de Valence, 2h
              de Lyon et 2h30 de Marseille</strong>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pourquoi ici ? */}
      <section className="bg-white border-y border-[#e8dfc6]">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-4">Pourquoi ici</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-light">La Drôme, terre de spiritualité discrète</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#e8dfc6] flex items-center justify-center mx-auto mb-5">
                <Wind className="w-6 h-6 text-[#8b7d5e]" />
              </div>
              <h3 className="font-serif text-lg mb-3">Un souffle rare</h3>
              <p className="text-sm text-[#5d5240] leading-relaxed">
                La Drôme provençale offre une lumière et un silence qu'on ne trouve nulle part ailleurs. Le climat sec de la vallée du Rhône, la proximité du Vercors et des Baronnies, l'air chargé de plantes aromatiques&nbsp;: le cadre parfait pour ralentir vraiment.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#e8dfc6] flex items-center justify-center mx-auto mb-5">
                <Leaf className="w-6 h-6 text-[#8b7d5e]" />
              </div>
              <h3 className="font-serif text-lg mb-3">Une nature vivante</h3>
              <p className="text-sm text-[#5d5240] leading-relaxed">
                Champs de lavande à perte de vue, oliveraies séculaires, chemins de randonnée entre thym sauvage et cyprès. Chaque marche silencieuse devient une méditation naturelle. Un terrain idéal pour les <Link to="/retraites" className="underline hover:text-[#2a2418]">séjours spirituels</Link> qu'on propose.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#e8dfc6] flex items-center justify-center mx-auto mb-5">
                <Compass className="w-6 h-6 text-[#8b7d5e]" />
              </div>
              <h3 className="font-serif text-lg mb-3">Facile d'accès</h3>
              <p className="text-sm text-[#5d5240] leading-relaxed">
                Gare TGV de Valence à 1h en voiture, Lyon Part-Dieu à 2h, aéroport de Marseille-Provence à 2h30. On récupère les participants venus en train à la gare la plus proche. Beaucoup de retraitants viennent de <strong>Paris, Lyon, Marseille, Genève ou Grenoble</strong>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ce qui se vit */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-6">Ce qui se vit ici</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light mb-8">Une pratique ancrée, jamais dogmatique</h2>
        <p className="text-lg text-[#5d5240] leading-relaxed max-w-2xl mx-auto mb-6">
          Nos retraites spirituelles en Drôme sont pensées comme une <strong>pause de reconnexion</strong>&nbsp;:
          méditations guidées au lever du soleil, soins énergétiques individuels, ateliers de
          numérologie ou d'écriture intuitive, marches silencieuses en pleine nature, repas
          végétariens partagés.
        </p>
        <p className="text-base text-[#5d5240] leading-relaxed max-w-2xl mx-auto italic mb-8">
          Pas de dogme religieux, pas de mysticisme flou. Une approche bienveillante, respectueuse
          de chaque parcours — que tu sois débutant·e ou déjà engagé·e dans une démarche
          intérieure.
        </p>
        <Link
          to="/retraites#temoignages"
          className="inline-flex items-center gap-2 text-sm text-[#8b7d5e] hover:text-[#2a2418] transition-colors font-medium underline underline-offset-4"
          data-testid="landing-drome-cta-testimonials"
        >
          ⭐ Lire les retours des précédentes participant·es
        </Link>
      </section>

      {/* CTA vers page pilier */}
      <section className="bg-[#2a2418] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Sparkles className="w-8 h-8 text-[#F5D8A2] mx-auto mb-6" />
          <h2 className="font-serif text-3xl sm:text-4xl font-light mb-6">Prêt·e à vivre l'expérience&nbsp;?</h2>
          <p className="text-white/70 mb-10 max-w-xl mx-auto">
            {nextRetreat
              ? `Le prochain séjour se tient au ${nextRetreat.location_name || "Mas d'Archas"} — ${formatDates(nextRetreat.start_date, nextRetreat.end_date)}. Il reste des places, à saisir avec un acompte de 30 %.`
              : "Découvre nos prochaines dates de séjours spirituels en Drôme provençale et réserve ta place avec un acompte de 30 %."}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/retraites"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#F5D8A2] text-[#2a2418] rounded-full text-sm tracking-widest uppercase font-medium hover:bg-white transition-all"
              data-testid="landing-drome-cta-retreats"
            >
              Voir les dates <ArrowRight className="w-4 h-4" />
            </Link>
            {nextRetreat && (
              <Link
                to={`/retraites/${nextRetreat.slug}`}
                className="inline-flex items-center gap-2 px-8 py-3 border border-white/30 text-white rounded-full text-sm tracking-widest uppercase hover:bg-white/10 transition-all"
                data-testid="landing-drome-cta-next"
              >
                <Calendar className="w-4 h-4" /> Prochain séjour
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* FAQ courte locale */}
      <section className="max-w-3xl mx-auto px-6 py-20">
        <h2 className="font-serif text-3xl mb-10 text-center">Questions fréquentes</h2>
        <div className="space-y-5">
          <details className="bg-white border border-[#e8dfc6] rounded-lg p-5 group">
            <summary className="cursor-pointer font-medium text-[#2a2418]">Comment venir en Drôme provençale sans voiture&nbsp;?</summary>
            <p className="mt-3 text-sm text-[#5d5240] leading-relaxed">
              La gare TGV Valence-Ville est à 1h en voiture du lieu de retraite. On organise des
              covoiturages entre participants, et je viens personnellement chercher les retraitants
              venus en train à la gare la plus proche. Depuis Paris, compte 2h20 de TGV jusqu'à Valence.
            </p>
          </details>
          <details className="bg-white border border-[#e8dfc6] rounded-lg p-5 group">
            <summary className="cursor-pointer font-medium text-[#2a2418]">Quelle est la meilleure saison pour une retraite en Drôme&nbsp;?</summary>
            <p className="mt-3 text-sm text-[#5d5240] leading-relaxed">
              Le printemps (avril-juin) et l'automne (septembre-octobre) sont particulièrement doux
              en Drôme provençale. Les journées sont chaudes sans être écrasantes, les paysages
              transfigurés par la lumière rasante. L'été apporte les champs de lavande en fleur,
              l'hiver un silence profond de terre qui dort.
            </p>
          </details>
          <details className="bg-white border border-[#e8dfc6] rounded-lg p-5 group">
            <summary className="cursor-pointer font-medium text-[#2a2418]">Faut-il déjà avoir une pratique spirituelle&nbsp;?</summary>
            <p className="mt-3 text-sm text-[#5d5240] leading-relaxed">
              Non. Nos retraites accueillent aussi bien des personnes qui n'ont jamais médité que
              des pratiquants confirmés. Chaque proposition est adaptée au rythme de chacun·e —
              on ne t'impose rien, tu prends ce qui te fait du bien.
            </p>
          </details>
        </div>
      </section>
    </div>
  );
}

function formatDates(start, end) {
  if (!start || !end) return "";
  const opts = { day: "numeric", month: "long", year: "numeric" };
  try {
    return `${new Date(start).toLocaleDateString("fr-FR", opts)} → ${new Date(end).toLocaleDateString("fr-FR", opts)}`;
  } catch {
    return `${start} → ${end}`;
  }
}
